import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, X, UserCog } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** EditUserModal — admin edits a client's personal data; the backend
 *  propagates the new name/phone to plans, receipts and PDFs. */
export const EditUserModal = ({ user, authHeader, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: "", username: "", email: "", phone: "", dob: "", country: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "", username: user.username || "", email: user.email || "",
      phone: user.phone || "", dob: user.dob || "", country: user.country || "",
    });
  }, [user]);

  if (!user) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError("");
    const body = {};
    for (const k of Object.keys(form)) {
      if ((form[k] || "") !== (user[k] || "")) body[k] = form[k];
    }
    if (!Object.keys(body).length) return onClose?.();
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/users/${user.id}/profile`, body, { headers: authHeader() });
      const prop = r.data.propagated || {};
      const n = Object.values(prop).reduce((s, v) => s + Number(v || 0), 0);
      toast.success(n ? `Datos actualizados · ${n} registro(s) sincronizados (planes, recibos, PDFs)` : "Datos actualizados");
      onSaved?.(r.data.user);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[160] bg-black/85 backdrop-blur-md flex items-center justify-center px-3" data-testid="edit-user-modal" onClick={onClose}>
      <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a0510] to-black border-2 border-[#d4af37]/50 rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <button data-testid="edit-user-close" onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 mb-1">
          <UserCog className="w-5 h-5 text-[#d4af37]" />
          <h3 className="font-serif text-lg text-white">Editar datos del usuario</h3>
        </div>
        <p className="text-xs text-neutral-400 mb-4">
          ID <span className="font-mono text-[#d4af37]">{user.visible_id}</span>. Los cambios se reflejan en planes, recibos, contratos y PDFs.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-neutral-400">Nombre completo</Label>
            <Input data-testid="edit-user-name" value={form.name} onChange={set("name")} className="bg-black/40 border-white/10 text-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-400">Usuario</Label>
              <Input data-testid="edit-user-username" value={form.username} onChange={set("username")} className="bg-black/40 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-400">Teléfono (WhatsApp)</Label>
              <Input data-testid="edit-user-phone" value={form.phone} onChange={set("phone")} placeholder="+52 662 000 0000" className="bg-black/40 border-white/10 text-white" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-neutral-400">Correo</Label>
            <Input data-testid="edit-user-email" type="email" value={form.email} onChange={set("email")} className="bg-black/40 border-white/10 text-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-400">Fecha de nacimiento</Label>
              <Input data-testid="edit-user-dob" type="date" value={form.dob} onChange={set("dob")} className="bg-black/40 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-400">País</Label>
              <Input data-testid="edit-user-country" value={form.country} onChange={set("country")} className="bg-black/40 border-white/10 text-white" />
            </div>
          </div>
          {error && <div data-testid="edit-user-error" className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2">{error}</div>}
          <button data-testid="edit-user-save" disabled={busy} onClick={save} className="w-full py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest font-bold bg-[#d4af37] hover:bg-[#f3d878] text-black disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
